/** 다시 조회 간격 — 견적 그림은 요청 커밋 뒤 worker 가 보통 몇 초 안에 그린다(견적 디자인 미리보기 설계 D14). */
export const QUOTE_THUMBNAIL_POLL_INTERVAL_MS = 3_000;
/** 요청 뒤 이만큼만 기다린다 — 그릴 수 없는 견적이 계속 조회하지 않게 한다. */
export const QUOTE_THUMBNAIL_POLL_WINDOW_MS = 2 * 60_000;
/**
 * 한 조회가 받는 응답 수 상한(첫 응답 포함, 성공·실패 합) — 기다림 상한(2분) ÷ 간격(3초) = 40번.
 * 브라우저 시계가 틀려 요청 시각이 미래로 보여도, 요청 시각이 없는 견적 요청 폼도 이만큼 받으면 멈춘다.
 */
export const QUOTE_THUMBNAIL_POLL_MAX_UPDATES =
  QUOTE_THUMBNAIL_POLL_WINDOW_MS / QUOTE_THUMBNAIL_POLL_INTERVAL_MS;

export interface QuoteThumbnailPollTarget {
  readonly thumbnailUrl: string | null;
  readonly createdAt: string;
}

/** 다시 조회 여부를 가르는 조회 상태 — TanStack Query `query.state` 를 그대로 넘긴다. 응답 수는 그 조회(캐시 항목)의 누적이다. */
export interface QuoteThumbnailPollState {
  readonly status: 'pending' | 'error' | 'success';
  readonly dataUpdateCount: number;
  readonly errorUpdateCount: number;
}

/**
 * 계속 조회해도 되는지 — 다시 조회가 실패한 상태면 멈춘다(실패는 성공 응답 수에 잡히지 않아 상한에 닿지 않는다).
 * 받은 값은 그대로 두므로 화면은 바뀌지 않는다. 성공·실패 응답을 합해 상한에 닿아도 멈춘다.
 */
const canKeepPolling = (state: QuoteThumbnailPollState): boolean =>
  state.status !== 'error' &&
  state.dataUpdateCount + state.errorUpdateCount < QUOTE_THUMBNAIL_POLL_MAX_UPDATES;

const isAwaiting = (target: QuoteThumbnailPollTarget, now: number): boolean => {
  if (target.thumbnailUrl !== null) return false;
  const createdAt = Date.parse(target.createdAt);
  if (Number.isNaN(createdAt)) return false;
  return now - createdAt < QUOTE_THUMBNAIL_POLL_WINDOW_MS;
};

/** 그림이 아직 없고 요청한 지 상한 안인 견적이 하나라도 있으면(그리고 조회를 계속해도 되면) 다시 조회할 간격, 아니면 false. */
export const quoteThumbnailRefetchInterval = (
  targets: readonly QuoteThumbnailPollTarget[],
  now: number,
  state: QuoteThumbnailPollState,
): number | false =>
  canKeepPolling(state) && targets.some((target) => isAwaiting(target, now))
    ? QUOTE_THUMBNAIL_POLL_INTERVAL_MS
    : false;

/**
 * 견적 요청 폼(원본 디자인의 지금 썸네일) — 에디터는 저장 직후 넘어오므로 그림이 아직 없을 수 있다.
 * 요청 시각이 없어 응답 수·실패 상태로만 자른다.
 */
export const designThumbnailRefetchInterval = (
  hasThumbnail: boolean,
  state: QuoteThumbnailPollState,
): number | false =>
  !hasThumbnail && canKeepPolling(state) ? QUOTE_THUMBNAIL_POLL_INTERVAL_MS : false;
